export type CaptureOverheadPngParams = {
	container: HTMLElement;
	scale?: number;
};

const FETCH_TIMEOUT_MS = 8000;

/**
 * Fetch the remote URL and return a `data:` URL with the resource inlined.
 * Returns the original URL on failure so the caller can fall back gracefully.
 * `cache: 'reload'` bypasses a poisoned no-cors cache entry left by a plain `<img>` display.
 */
export const fetchAsDataUrl = async (url: string, cache: RequestCache = 'default'): Promise<string> => {
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
	try {
		const response = await fetch(url, { mode: 'cors', cache, signal: controller.signal });
		if (!response.ok) return url;
		const blob = await response.blob();
		return await new Promise<string>((resolve, reject) => {
			const reader = new FileReader();
			reader.onloadend = () => resolve(reader.result as string);
			reader.onerror = () => reject(reader.error);
			reader.readAsDataURL(blob);
		});
	} catch {
		return url;
	} finally {
		clearTimeout(timer);
	}
};

/**
 * Replace every `<image href="https://…">` inside the SVG with a `data:` URL.
 *
 * Why: html2canvas (and the `<img src=svg-blob-url>` path used below) renders the
 * SVG by re-fetching every external resource inside an isolated context. Even when
 * the same texture loads fine in the original DOM, the re-fetch typically fails
 * silently (CORS preflight or the canvas tainting rules), giving a textureless
 * capture that looks like manufacturing view. Inlining the bytes sidesteps the
 * re-fetch entirely.
 */
const inlineSvgImageHrefs = async (svg: SVGSVGElement): Promise<void> => {
	const images = Array.from(svg.querySelectorAll('image')) as Array<SVGImageElement>;
	await Promise.all(images.map(async (img) => {
		const href = img.getAttribute('href') ?? img.getAttribute('xlink:href');
		if (!href || href.startsWith('data:')) return;
		const dataUrl = await fetchAsDataUrl(href);
		img.setAttribute('href', dataUrl);
		img.removeAttribute('xlink:href');
	}));
};

/**
 * Rasterizes the off-screen `<svg>` produced by `<RtaBlueprintComponent>` in
 * Overhead mode to a PNG data URL.
 *
 * The pipeline: inline texture images as data URLs → serialize SVG → load into
 * an `<img>` via blob URL → draw onto a 2D canvas. This intentionally skips
 * html2canvas because that library re-clones the DOM into an iframe and
 * re-fetches external resources, which dropped the countertop texture from
 * the capture. Since the on-screen overlays (READ-ONLY badge, view title) are
 * HTML siblings outside the SVG, serializing just the SVG naturally excludes
 * them — `showState`/`showTitle` still gate them as a belt-and-suspenders.
 */
export const captureOverheadPng = async (params: CaptureOverheadPngParams): Promise<string> => {
	const { container, scale = 2 } = params;

	const svg = container.querySelector('svg') as SVGSVGElement | null;
	if (!svg) throw new Error('captureOverheadPng: no <svg> found inside container');

	await inlineSvgImageHrefs(svg);

	const rect = svg.getBoundingClientRect();
	const width = Math.max(1, Math.round(rect.width));
	const height = Math.max(1, Math.round(rect.height));

	// Explicit width/height on the root <svg> is required for `<img src=svg-blob>`
	// to rasterize at the correct intrinsic size in every browser.
	svg.setAttribute('width', String(width));
	svg.setAttribute('height', String(height));

	const svgStr = new XMLSerializer().serializeToString(svg);
	const svgBlob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
	const svgUrl = URL.createObjectURL(svgBlob);

	try {
		const img = await new Promise<HTMLImageElement>((resolve, reject) => {
			const el = new Image();
			el.onload = () => resolve(el);
			el.onerror = () => reject(new Error('captureOverheadPng: failed to load serialized SVG'));
			el.src = svgUrl;
		});

		const canvas = document.createElement('canvas');
		canvas.width = width * scale;
		canvas.height = height * scale;
		const ctx = canvas.getContext('2d');
		if (!ctx) throw new Error('captureOverheadPng: no 2D context');
		ctx.fillStyle = '#FFFFFF';
		ctx.fillRect(0, 0, canvas.width, canvas.height);
		ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

		// WebP encodes this line art ~10x smaller; browsers without webp encoding silently return PNG
		const webp = canvas.toDataURL('image/webp', 0.92);
		if (webp.startsWith('data:image/webp')) return webp;
		return canvas.toDataURL('image/png');
	} finally {
		URL.revokeObjectURL(svgUrl);
	}
};
